/* eslint-disable @angular-eslint/component-class-suffix */
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { ChangeDetectionStrategy, Component, inject, signal, WritableSignal } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { format } from 'date-fns';
import { firstValueFrom } from 'rxjs';
import { FuseConfirmationService } from '../../../@fuse/services/confirmation/confirmation.service';
import { ServicesService } from '../../modules/admin/services/services.service';

@Component({
    selector: 'sync-workplace-users-dialog',
    templateUrl: './sync-workplace-users-dialog.component.html',
    imports: [FormsModule, MatButtonModule, MatChipsModule, MatFormFieldModule, MatIconModule, ReactiveFormsModule, TranslocoModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class SyncWorkplaceUsersDialog {

    mails: WritableSignal<string[]> = signal([]);
    mailsControl: FormControl = new FormControl<string[]>([]);
    
    readonly separators: number[] = [ENTER, COMMA];

    private data: { serviceId: string, date: Date } = inject(MAT_DIALOG_DATA);
    private dialogRef = inject(MatDialogRef<SyncWorkplaceUsersDialog>);
    private _fuseConfirmationService = inject(FuseConfirmationService);
    private servicesService = inject(ServicesService);
    private transloco = inject(TranslocoService);

    async sync() {
        const confirmDialog = this.openConfirmDialog();
        const confirm = await firstValueFrom(confirmDialog.afterClosed());
        if (confirm === 'confirmed') {
            const reply = await firstValueFrom(this.servicesService.synchronizeServiceWorkplaceUserAssignment({
                serviceId: this.data.serviceId,
                mails: this.mails() ?? [],
                date: format(this.data.date, 'yyyy-MM-dd')
            }));
            this.closeDialog();
        }
    }

    remove(mail: string) {
        this.mails.update(mails => {
            const index = mails.indexOf(mail);
            if (index < 0) {
                return mails;
            }
            mails.splice(index, 1);
            return [...mails];
        });
    }

    add(event: MatChipInputEvent): void {
        const value = (event.value || '').trim();

        // Add our keyword
        if (value) {
            this.mails.update(keywords => [...keywords, value]);
        }

        // Clear the input value
        event.chipInput!.clear();
    }

    closeDialog() {
        this.dialogRef.close();
    }

    private openConfirmDialog() {
        return this._fuseConfirmationService.open({
            title: this.transloco.translate('warning'),
            message: this.transloco.translate('services.workplaces.confirmSync'),
            actions: {
                confirm: {
                    label: this.transloco.translate('confirm')
                },
                cancel: {
                    label: this.transloco.translate('cancel')
                }
            }
        });
    }

}
